import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Alert, Button, ModalContent } from '@clearcover/atlas';
import { useLazyApi } from 'Shared/services/Api';
import { Card, CardSectionInfoPairs, FormInput } from 'Shared/components';
import { formatNumberToOrdinal, isPresent } from 'Shared/utils';
import { PolicyContext } from 'Policies/utils';
import { ordinalDayOptions } from 'Policies/components/PolicyPaymentAndBilling/EditBillingInfo/EditAutoPaySchedule/EditAutoPaySchedule';
import { AutoPayScheduleTable } from './AutopayScheduleTable';
import styles from './styles.module.css';

export type EditAutoPayScheduleFormPropsType = {
  policyId: string;
  close(): void;
  onSuccess(message: string): void;
  currentDueDay?: number;
  currentAutoPayDraftDay?: number;
};

export const EditAutoPayScheduleForm: FC<EditAutoPayScheduleFormPropsType> = ({
  close,
  onSuccess,
  policyId,
  currentDueDay,
  currentAutoPayDraftDay,
}) => {
  const { refetchHandler } = useContext(PolicyContext);

  const [{ data, loading }, getAutoPayDraftSchedule] = useLazyApi<{
    paymentSchedule: Array<{
      amount: { value: number };
      draftDate: string;
      type: string;
    }>;
  }>({
    url: '/payments/getAutoPayDraftSchedule',
    method: 'POST',
  });

  const [
    { loading: updateAutoPayScheduleLoading },
    updateAutoPayDraftSchedule,
  ] = useLazyApi<{
    paymentSchedule: Array<{
      amount: { value: number };
      draftDate: string;
    }>;
  }>({
    url: '/payments/updateAutoPayDraftSchedule',
    method: 'POST',
  });

  const submitForm = useCallback(
    (
      values: { day: number },
      { setStatus, setSubmitting }: FormikHelpers<{ day: number }>
    ) => {
      updateAutoPayDraftSchedule({
        data: {
          policyId,
          day: values.day,
        },
      })
        .then(() => {
          setSubmitting(false);
          refetchHandler();
          onSuccess('Autopay date successfully updated.');
          close();
        })
        .catch(err => {
          setSubmitting(false);
          setStatus(err.response.data.message ?? err.message);
        });
    },
    [updateAutoPayDraftSchedule, policyId, refetchHandler, onSuccess, close]
  );

  const AUTOPAY_FORM_SCHEMA = Yup.object().shape({
    day: Yup.number()
      .integer()
      .min(1, 'Required')
      .required('Required'),
  });

  const displayedInstallments = useMemo(() => {
    if (!data) return [];
    return (
      data.paymentSchedule
        // Only show upcoming payments
        .filter(i => i.type === 'INSTALLMENT')
        .map(i => ({ date: i.draftDate, amount: i.amount.value }))
    );
  }, [data]);

  useEffect(() => {
    // On load, get the current autopay draft schedule
    if (!data && !loading) {
      getAutoPayDraftSchedule({
        data: { day: currentAutoPayDraftDay, policyId },
      });
    }
  }, [
    currentAutoPayDraftDay,
    data,
    getAutoPayDraftSchedule,
    loading,
    policyId,
  ]);

  return (
    <Formik
      initialValues={{ day: 0 }}
      onSubmit={submitForm}
      validationSchema={AUTOPAY_FORM_SCHEMA}
    >
      {({ setFieldValue, handleSubmit, isSubmitting, status, errors }) => (
        <div
          className={styles.wrapper}
          data-testid="edit-autopay-schedule-form"
        >
          <h4>Update Autopay Date</h4>
          <p>
            Update the customer's autopay date to their preferred payment date
            each month to best align with their needs and cash flow. Customers
            can also manage their autopay date from the mobile app if they need
            to make changes.
          </p>
          <p>
            <strong>Please Note:</strong> The customer's payment due date will
            remain the same as the policy effective date, and failure to make an
            on-time payment will cancel the customer's policy.
          </p>
          <ModalContent>
            <Card className={styles.autoPayScheduleSelectionContainer}>
              <CardSectionInfoPairs
                data={[currentDueDay, currentAutoPayDraftDay].map(date =>
                  date ? `${formatNumberToOrdinal(date)} of each month` : '-'
                )}
                headers={['Due Date', 'Autopay Date']}
                dataFontStyle="none"
                copyable={false}
              />
              <FormInput
                data-testid="new-autopay-date"
                label="New autopay date"
                options={ordinalDayOptions}
                disabled={loading || updateAutoPayScheduleLoading}
                variant="select"
                className={styles.formInput}
                onChange={(value: string) => {
                  setFieldValue('day', +value);
                  getAutoPayDraftSchedule({
                    data: { day: +value, policyId },
                  });
                }}
                errorMessage={errors.day}
              />
            </Card>
            <Card
              data-testid={'autopay-schedule'}
              className={classNames(styles.autoPaySchedule, {
                [styles.loading]: loading,
              })}
            >
              <div className={styles.scheduleHeader}>
                <h4>Current Payment Schedule</h4>
                <p>
                  <strong>Please Note:</strong> Premium amount does not include
                  applicable fees.
                </p>
              </div>
              <AutoPayScheduleTable
                items={displayedInstallments}
                loading={loading}
              />
            </Card>
          </ModalContent>
          <div className={styles.actions}>
            {isPresent(status) && (
              <Alert className={styles.error} message={status} />
            )}
            <Button
              variant="secondary"
              onClick={close}
              data-testid="congrats-update-autopay-schedule-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isSubmitting={isSubmitting}
              onClick={() => handleSubmit()}
              data-testid="congrats-update-autopay-schedule-submit"
            >
              Update
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
};
